import { FC } from "react";
import { useMatch } from "react-router-dom";
import CompanyArea from "@/components/headerbar/companyArea";
import PortalNameArea from "@/components/headerbar/portalNameArea";
import "@/components/styles/headerBar.css";
import {
  AppBar,
  CircularProgress,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HeaderBackground from "@/icons/headerBackground";
import { renderToStaticMarkup } from "react-dom/server";
import MenuIcon from "@mui/icons-material/Menu";
import AccountSettings from "@/components/headerbar/accountSettings";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import HeaderBarQuickActionsWrapper from "@/components/headerbar/headerBarQuickActionsWrapper";
import QuickLinkWrapper from "@/components/headerbar/quickLinkWrapper";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import LanguageQuickSettings from "@/components/headerbar/languageSettings";
import UpdateAvailableIcon from "@/components/headerbar/updateAvailableIcon";
import { useUpdateAvailableNotification } from "@/context/updateAvailableNotificationContext";

interface Props {
  toggleDrawer: (open: boolean) => any;
  selectedCompany: string;
  handleOpenSettings?: (event: any) => void;
  handleCloseSettings?: (event: any) => void;
  handleNavigateSettings?: () => void;
  combinedSignout: () => void;
  signOut: () => void;
  user?: any;
  email?: any;
  openSettings: boolean;
  firstNameInitial?: string;
  lastNameInitial?: string;
  anchorEl?: any;
  subdomain: string;
}

const HeaderBar: FC<Props> = ({
  toggleDrawer,
  selectedCompany,
  handleOpenSettings,
  handleCloseSettings,
  handleNavigateSettings,
  combinedSignout,
  signOut,
  openSettings,
  firstNameInitial,
  lastNameInitial,
  anchorEl,
  user,
  email,
  subdomain,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const appointmentFormRoute = useMatch("/appointmentDashboard/:appointmentId/:formId/:findingId?");
  const isAppointmentFormView = subdomain === "admin" && Boolean(appointmentFormRoute);

  const svgString = encodeURIComponent(
    renderToStaticMarkup(<HeaderBackground />),
  );

  const logedInUserInfo: any = useSelector((state: RootState) => state.login);
  const backgroundTasks = useSelector(
    (state: RootState) => state.backgroundTasks.tasks,
  );
  const pageHeading = useSelector(
    (state: RootState) => state.pageHeading.heading,
  );
  const {
    showUpdateIcon,
    onUpdateIconHover,
    onUpdateIconClick,
    onUpdateIconMouseLeave,
  } = useUpdateAvailableNotification();

  const runningTasks = backgroundTasks.filter(
    (task) => task.status === "in_progress",
  );
  const tooltipTitle =
    runningTasks.length > 0
      ? runningTasks.map((task) => task.description).join("\n")
      : "";

  return (
    <>
      <AppBar position="static" className="appBarContainer">
        <Toolbar
          className={`toolbarContainer${isAppointmentFormView ? " appointmentFormHeader" : ""}`}
          sx={{
            background: `url('data:image/svg+xml;utf8, ${svgString}')`,
          }}
        >
          <div className="leftContentWrapper">
            <GenericButton
              onClick={toggleDrawer(true)}
              variant="icon"
              color="white"
              className="menuButton"
              data-testid="open-drawer-button"
            >
              <MenuIcon />
            </GenericButton>
            <div className="headerBarTextContainer">
              <CompanyArea companyName={selectedCompany} />
            </div>
          </div>
          {pageHeading && (
            <div className="pageHeadingWrapper">
              <Tooltip title={pageHeading} arrow>
                <Typography
                  component="h1"
                  className="pageHeading"
                  data-testid="page-heading"
                >
                  {pageHeading}
                </Typography>
              </Tooltip>
            </div>
          )}
          <div className="rightContentWrapper">
            {showUpdateIcon && (
              <UpdateAvailableIcon
                onHover={onUpdateIconHover}
                onClick={onUpdateIconClick}
                onMouseLeave={onUpdateIconMouseLeave}
              />
            )}
            {runningTasks.length > 0 && (
              <Tooltip title={tooltipTitle} arrow>
                <div className="backGroundTasksWrapper">
                  <CircularProgress size={24} sx={{ color: "#ff6428" }} />
                </div>
              </Tooltip>
            )}
            <LanguageQuickSettings
              currentLang={logedInUserInfo.preferred_language}
              userId={logedInUserInfo.userId}
            />
            <HeaderBarQuickActionsWrapper subdomain={subdomain} />
            {logedInUserInfo.group_set.includes("manager") &&
              subdomain === "portal" && (
                <QuickLinkWrapper navigateTo="managerPortal" />
              )}
            {subdomain === "manage" && (
              <QuickLinkWrapper navigateTo="userPortal" />
            )}
            {!isTablet && <PortalNameArea />}
            <AccountSettings
              handleOpenSettings={handleOpenSettings}
              handleCloseSettings={handleCloseSettings}
              handleNavigateSettings={handleNavigateSettings}
              combinedSignout={combinedSignout}
              signOut={signOut}
              open={openSettings}
              firstNameInitial={firstNameInitial}
              lastNameInitial={lastNameInitial}
              anchorEl={anchorEl}
              user={user}
              email={email}
            />
          </div>
        </Toolbar>
      </AppBar>
    </>
  );
};

export default HeaderBar;
